import React, {useState} from 'react';

import {createAnalysis} from 'analysis-tool/AnalysisActions';
import {DataCatalogFileVariableName} from 'analysis-tool/AnalysisStepConfigVariableNames';
import {AnalysisTemplateSelectorTemplate} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import SimpleAnalysisTemplateSelector from 'analysis-tool/templates/SimpleAnalysisTemplateSelector';
import {assert} from 'Asserts';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import CreateAnalysisForm from './CreateAnalysisForm';
import useCreateAnalysisState from './useCreateAnalysisState';

export interface RunAnalysisTemplateModalProps {
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  inputFileId: string;
  initialAnalysisName?: string;
}

/**
 * Modal to select an analysis template to run, from source data.
 *
 * Currently, the source data must be a Data Catalog file, and only templates whose sole variable is a
 * `fileId` config field can be selected. In the future, this modal could support templates with a greater
 * variety of inputs.
 */
export default function RunAnalysisTemplateModal({
  onRequestSubmit,
  onRequestClose,
  inputFileId,
  initialAnalysisName,
}: RunAnalysisTemplateModalProps): JSX.Element {
  const {createAnalysisState, setCreateAnalysisState, requestParamsInfo} = useCreateAnalysisState({
    initialAnalysisName,
  });
  const [template, setTemplate] = useState<AnalysisTemplateSelectorTemplate | null>(null);

  async function handleRequestSubmit(): Promise<void> {
    assert(requestParamsInfo.isComplete, 'Expected create analysis request params to be complete');
    assert(template != null, 'Expected template to be selected');
    FolderDefaults.set(FolderDefaultsKey.ANALYSIS, requestParamsInfo.requestParams.folderId);

    const templateRun = {
      templateId: template.id,
      variableBindings: [{variableId: template.configVariables[0].id, variableValue: inputFileId}],
    };
    const params = {...requestParamsInfo.requestParams, templateRun};
    await withLoadingScreen(createAnalysis(params), 'Creating analysis...');

    onRequestSubmit();
  }

  return (
    <Modal
      title="Run analysis template"
      onRequestSubmit={handleRequestSubmit}
      onRequestClose={onRequestClose}
      buttons={[
        {
          submit: true,
          text: 'Create',
          style: ButtonStyle.SECONDARY,
          disabled: !requestParamsInfo.isComplete || template == null,
        },
      ]}
    >
      <div className="u-spacing-bottom">
        <FieldLabel label="Analysis Template" isRequired={true} />
        <SimpleAnalysisTemplateSelector
          template={template}
          onSelectTemplate={setTemplate}
          filterTemplate={(template) =>
            template.dataFrameVariables.length === 0 &&
            template.configVariables.length === 1 &&
            template.configVariables[0].configVariableKey === DataCatalogFileVariableName.FILE_ID
          }
        />
      </div>
      <CreateAnalysisForm
        createAnalysisState={createAnalysisState}
        setCreateAnalysisState={setCreateAnalysisState}
        hideDataSharingWarningBanner={false}
      />
    </Modal>
  );
}
